import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { pricingApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import {
  BadgeDollarSign, CreditCard, Package, SlidersHorizontal, Percent,
  Settings2, History, FlaskConical, ChevronLeft, Plus, RefreshCw,
  Pencil, Archive, Send, CheckCircle2, AlertCircle, Clock, Trash2,
  ChevronDown, ChevronUp, Info, X, Save, Eye
} from "lucide-react";

// ── Tab definition ────────────────────────────────────────────────────────────
const TABS = [
  { id: "rate-cards",      label: "Rate Cards",        icon: CreditCard },
  { id: "service-pricing", label: "Service Pricing",   icon: BadgeDollarSign },
  { id: "adjustments",     label: "Pricing Rules",     icon: SlidersHorizontal },
  { id: "tax-currency",    label: "Tax & Currency",    icon: Percent },
  { id: "estimate-config", label: "Estimate Config",   icon: Settings2 },
  { id: "version-history", label: "Version History",   icon: History },
  { id: "simulator",       label: "Pricing Simulator", icon: FlaskConical },
];

// ── Monetary helpers ──────────────────────────────────────────────────────────
const formatMinor = (minor, currency = "INR") => {
  if (minor == null) return "—";
  const val = minor / 100;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(val);
};
const bpsToLabel = (bps) => bps != null ? `${(bps / 100).toFixed(2)}%` : "—";

// ── Status helpers ─────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  DRAFT:        "bg-amber-50 text-amber-700 border-amber-200",
  UNDER_REVIEW: "bg-blue-50 text-blue-700 border-blue-200",
  PUBLISHED:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  ARCHIVED:     "bg-slate-100 text-slate-500 border-slate-200",
  ACTIVE:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  INACTIVE:     "bg-slate-100 text-slate-500 border-slate-200",
};
const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
    {status?.replace(/_/g, " ")}
  </span>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function PricingStudio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("rate-cards");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    pricingApi.getConfig()
      .then(r => setConfig(r.data))
      .catch(() => toast.error("Failed to load pricing configuration"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/setup-studio")}
            className="p-1.5 rounded-lg text-[#706E6B] hover:text-[#0176D3] hover:bg-[#EEF4FF] transition-all"
            data-testid="pricing-studio-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 bg-[#EEF4FF] rounded-lg flex items-center justify-center">
            <BadgeDollarSign className="w-4 h-4 text-[#0176D3]" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#181818] font-sans">Pricing Studio</h1>
            <p className="text-xs text-[#706E6B]">Setup Studio · Domain 08 · {user?.tenant_name}</p>
          </div>
          <div className="ml-auto flex items-center gap-4">
            {config?.summary && (
              <div className="hidden md:flex items-center gap-4 text-xs text-[#706E6B] font-mono">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  {config.summary.published_rate_cards} published
                </span>
                <span className="flex items-center gap-1">
                  <SlidersHorizontal className="w-3 h-3 text-blue-500" />
                  {config.summary.active_adjustments} rules
                </span>
                <span className="flex items-center gap-1">
                  <Percent className="w-3 h-3 text-amber-500" />
                  {config.summary.active_tax_mappings} tax configs
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b border-[#E5E5E5] px-6">
        <div className="max-w-7xl mx-auto flex gap-0 overflow-x-auto no-scrollbar">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                data-testid={`pricing-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
                  active
                    ? "border-[#0176D3] text-[#0176D3]"
                    : "border-transparent text-[#706E6B] hover:text-[#444444]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {activeTab === "rate-cards"      && <RateCardsTab config={config} />}
        {activeTab === "service-pricing" && <ServicePricingTab config={config} />}
        {activeTab === "adjustments"     && <AdjustmentsTab config={config} />}
        {activeTab === "tax-currency"    && <TaxCurrencyTab />}
        {activeTab === "estimate-config" && <EstimateConfigTab />}
        {activeTab === "version-history" && <VersionHistoryTab />}
        {activeTab === "simulator"       && <SimulatorTab />}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1: RATE CARDS
// ═══════════════════════════════════════════════════════════════════════════════
function RateCardsTab({ config }) {
  const [rateCards, setRateCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    pricingApi.listRateCards(statusFilter ? { status: statusFilter } : {})
      .then(r => setRateCards(r.data.rate_cards || []))
      .catch(() => toast.error("Failed to load rate cards"))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async (rcid) => {
    setBusy(true);
    try {
      await pricingApi.publishRateCard(rcid);
      toast.success("Rate card published successfully");
      load();
      if (selected?.id === rcid) setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish rate card");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitReview = async (rcid) => {
    setBusy(true);
    try {
      await pricingApi.submitForReview(rcid);
      toast.success("Rate card submitted for review");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit for review");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async (rcid) => {
    if (!window.confirm("Archive this rate card? It will no longer be selectable for new estimates.")) return;
    setBusy(true);
    try {
      await pricingApi.archiveRateCard(rcid);
      toast.success("Rate card archived");
      load();
      if (selected?.id === rcid) setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to archive rate card");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-[#181818]">Rate Cards</h2>
          <span className="text-xs text-[#706E6B] font-mono">({rateCards.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs border border-[#E5E5E5] rounded-lg px-3 py-1.5 bg-white text-[#444444]"
            data-testid="rc-status-filter"
          >
            <option value="">All Statuses</option>
            {(config?.rate_card_statuses || []).map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button onClick={load} className="p-1.5 border border-[#E5E5E5] rounded-lg text-[#706E6B] hover:text-[#0176D3]">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0176D3] text-white text-xs font-medium rounded-lg hover:bg-[#014486] transition-all"
            data-testid="create-rate-card-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New Rate Card
          </button>
        </div>
      </div>

      {loading ? <TableSkeleton rows={4} /> : (
        rateCards.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title="No rate cards yet"
            description="Create your first rate card to define pricing for guest estimates."
            action={{ label: "Create Rate Card", onClick: () => setShowCreate(true) }}
          />
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
            <table className="w-full text-xs" data-testid="rate-cards-table">
              <thead>
                <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Rate Card</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Currency</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Effective From</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Lines</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Version</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#444444]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rateCards.map(rc => (
                  <tr
                    key={rc.id}
                    data-testid={`rc-row-${rc.id}`}
                    className="border-b border-[#F3F3F3] hover:bg-[#F8F9FB] cursor-pointer"
                    onClick={() => setSelected(rc)}
                  >
                    <td className="px-4 py-3 font-medium text-[#181818]">{rc.display_name}</td>
                    <td className="px-4 py-3 font-mono text-[#706E6B]">{rc.rate_card_code}</td>
                    <td className="px-4 py-3"><StatusPill status={rc.status} /></td>
                    <td className="px-4 py-3 text-[#706E6B]">{rc.currency}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{rc.effective_from}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{rc.line_count ?? 0}</td>
                    <td className="px-4 py-3 text-[#706E6B]">v{rc.version}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        {rc.status === "DRAFT" && (
                          <button
                            onClick={() => handleSubmitReview(rc.id)}
                            disabled={busy}
                            className="px-2 py-1 text-[10px] font-medium border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50"
                            data-testid={`rc-submit-${rc.id}`}
                          >Submit</button>
                        )}
                        {(rc.status === "DRAFT" || rc.status === "UNDER_REVIEW") && (
                          <button
                            onClick={() => handlePublish(rc.id)}
                            disabled={busy}
                            className="px-2 py-1 text-[10px] font-medium border border-emerald-200 text-emerald-600 rounded-lg hover:bg-emerald-50"
                            data-testid={`rc-publish-${rc.id}`}
                          >Publish</button>
                        )}
                        {rc.status === "PUBLISHED" && (
                          <button
                            onClick={() => handleArchive(rc.id)}
                            disabled={busy}
                            className="px-2 py-1 text-[10px] font-medium border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-50"
                          >Archive</button>
                        )}
                        <button
                          onClick={() => setSelected(rc)}
                          className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#EEF4FF] rounded-lg"
                        ><Eye className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {showCreate && (
        <CreateRateCardModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {selected && (
        <RateCardDetailModal
          rcId={selected.id}
          config={config}
          onClose={() => { setSelected(null); load(); }}
          onPublish={handlePublish}
          onArchive={handleArchive}
          busy={busy}
        />
      )}
    </div>
  );
}

function CreateRateCardModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    rate_card_code: "", display_name: "", currency: "INR",
    effective_from: new Date().toISOString().split("T")[0],
    effective_to: "", notes: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.rate_card_code || !form.display_name || !form.effective_from) {
      toast.error("Rate Card Code, Name and Effective From are required");
      return;
    }
    setSaving(true);
    try {
      await pricingApi.createRateCard({ ...form, effective_to: form.effective_to || null });
      toast.success("Rate card created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create rate card");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create Rate Card" onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Rate Card Code *" help="Unique identifier e.g. RC-2026-Q4-WEDDING">
          <input
            value={form.rate_card_code}
            onChange={e => setForm(f => ({ ...f, rate_card_code: e.target.value.toUpperCase() }))}
            placeholder="RC-2026-Q4-WEDDING"
            className="input-field font-mono"
            data-testid="rc-code-input"
          />
        </FormField>
        <FormField label="Display Name *">
          <input
            value={form.display_name}
            onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
            placeholder="Wedding Services Rate Card Q4 2026"
            className="input-field"
            data-testid="rc-name-input"
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Currency">
            <input value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
              className="input-field font-mono uppercase" maxLength={3} />
          </FormField>
          <FormField label="Effective From *">
            <input type="date" value={form.effective_from}
              onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
              className="input-field" data-testid="rc-effective-from-input" />
          </FormField>
        </div>
        <FormField label="Effective To (optional)">
          <input type="date" value={form.effective_to}
            onChange={e => setForm(f => ({ ...f, effective_to: e.target.value }))}
            className="input-field" />
        </FormField>
        <FormField label="Notes">
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} className="input-field resize-none" placeholder="Optional notes..." />
        </FormField>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="rc-save-btn">
            {saving ? "Creating..." : "Create Rate Card"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RateCardDetailModal({ rcId, config, onClose, onPublish, onArchive, busy }) {
  const [rc, setRc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddLine, setShowAddLine] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    pricingApi.getRateCard(rcId)
      .then(r => setRc(r.data))
      .catch(() => toast.error("Failed to load rate card"))
      .finally(() => setLoading(false));
  }, [rcId]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async () => {
    try {
      const r = await pricingApi.getRateCardHistory(rcId);
      setHistory(r.data.history || []);
      setShowHistory(true);
    } catch {
      toast.error("Failed to load history");
    }
  };

  const removeLine = async (lid) => {
    try {
      await pricingApi.removeLine(rcId, lid);
      toast.success("Line removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove line");
    }
  };

  const canEdit = rc && (rc.status === "DRAFT" || rc.status === "UNDER_REVIEW");

  return (
    <Modal title={rc ? rc.display_name : "Rate Card"} onClose={onClose} wide>
      {loading ? <div className="h-40 flex items-center justify-center"><Spinner /></div> : !rc ? (
        <p className="text-sm text-red-500">Failed to load rate card.</p>
      ) : (
        <div className="space-y-5">
          {/* Header info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <InfoCard label="Code" value={rc.rate_card_code} mono />
            <InfoCard label="Status" value={<StatusPill status={rc.status} />} />
            <InfoCard label="Currency" value={rc.currency} mono />
            <InfoCard label="Effective From" value={rc.effective_from} />
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#181818]">
                Pricing Lines
                <span className="ml-2 text-xs font-normal text-[#706E6B] font-mono">
                  ({(rc.lines || []).length})
                </span>
              </h3>
              {canEdit && (
                <button
                  onClick={() => setShowAddLine(true)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-[#0176D3] text-white rounded-lg hover:bg-[#014486]"
                  data-testid="add-pricing-line-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Line
                </button>
              )}
            </div>
            {(rc.lines || []).length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                No pricing lines yet. Add at least one line before publishing.
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
                      <th className="text-left px-3 py-2 font-semibold text-[#444444]">Service</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#444444]">Model</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#444444]">Unit</th>
                      <th className="text-right px-3 py-2 font-semibold text-[#444444]">Base Amount</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#444444]">Notes</th>
                      {canEdit && <th className="px-3 py-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rc.lines.map(line => (
                      <tr key={line.line_id} data-testid={`line-row-${line.line_id}`}
                        className="border-b border-[#F3F3F3]">
                        <td className="px-3 py-2 font-medium text-[#181818]">
                          {line.service_name || line.service_code}
                          {line.is_price_on_request && (
                            <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-slate-100 text-slate-500 rounded">POR</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[#706E6B]">{line.pricing_model?.replace(/_/g, " ")}</td>
                        <td className="px-3 py-2 text-[#706E6B]">{line.unit}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#181818]">
                          {line.is_price_on_request ? "—" : formatMinor(line.base_amount_minor, line.currency)}
                        </td>
                        <td className="px-3 py-2 text-[#706E6B] truncate max-w-[120px]">{line.notes || "—"}</td>
                        {canEdit && (
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => removeLine(line.line_id)}
                              className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E5E5E5]">
            <button onClick={loadHistory} className="btn-secondary text-xs">
              <History className="w-3.5 h-3.5 mr-1" /> History
            </button>
            {rc.status === "DRAFT" && (
              <button onClick={() => onPublish(rc.id)} disabled={busy}
                className="btn-primary text-xs" data-testid={`rc-detail-publish-${rc.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish
              </button>
            )}
            {rc.status === "UNDER_REVIEW" && (
              <button onClick={() => onPublish(rc.id)} disabled={busy} className="btn-primary text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish
              </button>
            )}
            {rc.status === "PUBLISHED" && (
              <button onClick={() => onArchive(rc.id)} disabled={busy} className="btn-secondary text-xs">
                <Archive className="w-3.5 h-3.5 mr-1" /> Archive
              </button>
            )}
            <button onClick={onClose} className="btn-secondary text-xs ml-auto">Close</button>
          </div>

          {/* History panel */}
          {showHistory && history && (
            <div className="border border-[#E5E5E5] rounded-xl overflow-hidden">
              <div className="bg-[#F3F3F3] px-4 py-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-[#444444]">Status History</p>
                <button onClick={() => setShowHistory(false)} className="text-[#706E6B] hover:text-[#181818]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="divide-y divide-[#F3F3F3]">
                {history.map((h, i) => (
                  <div key={i} className="px-4 py-3 flex items-start gap-3 text-xs">
                    <StatusPill status={h.status} />
                    <div>
                      <p className="text-[#444444]">{h.note}</p>
                      <p className="text-[#706E6B] font-mono mt-0.5">
                        {h.changed_by_name} · {h.changed_at?.slice(0, 16).replace("T", " ")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showAddLine && (
            <AddRateLineModal
              rcId={rcId}
              config={config}
              onClose={() => setShowAddLine(false)}
              onAdded={() => { setShowAddLine(false); load(); }}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function AddRateLineModal({ rcId, config, onClose, onAdded }) {
  const [form, setForm] = useState({
    service_id: "",
    pricing_model: "FLAT_RATE",
    unit: "EVENT",
    base_amount_minor: "",
    currency: "INR",
    min_quantity: 1,
    max_quantity: "",
    is_price_on_request: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.service_id) { toast.error("Select a service"); return; }
    if (!form.is_price_on_request && (!form.base_amount_minor || isNaN(Number(form.base_amount_minor)))) {
      toast.error("Enter a valid base amount (in minor units, e.g. 500000 for ₹5000)");
      return;
    }
    setSaving(true);
    try {
      await pricingApi.addLine(rcId, {
        ...form,
        base_amount_minor: form.is_price_on_request ? 0 : Number(form.base_amount_minor),
        min_quantity: Number(form.min_quantity) || 1,
        max_quantity: form.max_quantity ? Number(form.max_quantity) : null,
        tier_breakpoints: [],
      });
      toast.success("Pricing line added");
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add line");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#0176D3]/20 bg-[#EEF4FF]/50 rounded-xl p-4 space-y-4">
      <h4 className="text-sm font-semibold text-[#181818]">Add Pricing Line</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Service *">
          <select value={form.service_id} onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}
            className="input-field" data-testid="line-service-select">
            <option value="">Select service...</option>
            {(config?.services || []).map(s => (
              <option key={s.id} value={s.id}>{s.name || s.code}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Pricing Model *">
          <select value={form.pricing_model} onChange={e => setForm(f => ({ ...f, pricing_model: e.target.value }))}
            className="input-field">
            {(config?.pricing_models || []).map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Unit">
          <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
            className="input-field">
            {(config?.units || []).map(u => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label={`Base Amount (minor units, ${form.currency})`} help="e.g. 500000 = ₹5,000.00">
          <input
            type="number"
            value={form.base_amount_minor}
            onChange={e => setForm(f => ({ ...f, base_amount_minor: e.target.value }))}
            disabled={form.is_price_on_request}
            className="input-field font-mono disabled:opacity-50"
            placeholder="500000"
            data-testid="line-amount-input"
          />
        </FormField>
        <FormField label="Min Quantity">
          <input type="number" value={form.min_quantity}
            onChange={e => setForm(f => ({ ...f, min_quantity: e.target.value }))}
            min={1} className="input-field" />
        </FormField>
        <FormField label="Max Quantity (optional)">
          <input type="number" value={form.max_quantity}
            onChange={e => setForm(f => ({ ...f, max_quantity: e.target.value }))}
            className="input-field" />
        </FormField>
      </div>
      <label className="flex items-center gap-2 text-xs text-[#444444]">
        <input type="checkbox" checked={form.is_price_on_request}
          onChange={e => setForm(f => ({ ...f, is_price_on_request: e.target.checked }))}
          className="rounded" data-testid="line-por-checkbox" />
        Price on Request (no amount shown to guest)
      </label>
      <FormField label="Notes">
        <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          className="input-field" placeholder="Optional notes..." />
      </FormField>
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="btn-secondary text-xs">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary text-xs" data-testid="add-line-save-btn">
          {saving ? "Adding..." : "Add Line"}
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2: SERVICE PRICING (shows lines from all published rate cards)
// ═══════════════════════════════════════════════════════════════════════════════
function ServicePricingTab({ config }) {
  const [rateCards, setRateCards] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    pricingApi.listRateCards({ status: "PUBLISHED" })
      .then(r => {
        const cards = r.data.rate_cards || [];
        setRateCards(cards);
        if (cards.length > 0) setSelected(cards[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  const [rcDetail, setRcDetail] = useState(null);
  useEffect(() => {
    if (!selected) return;
    pricingApi.getRateCard(selected).then(r => setRcDetail(r.data));
  }, [selected]);

  if (loading) return <TableSkeleton rows={4} />;

  if (rateCards.length === 0) return (
    <EmptyState
      icon={BadgeDollarSign}
      title="No published rate cards"
      description="Create and publish a Rate Card first to view service pricing lines."
    />
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-[#181818]">Service Pricing</h2>
        <select
          value={selected || ""}
          onChange={e => setSelected(e.target.value)}
          className="text-xs border border-[#E5E5E5] rounded-lg px-3 py-1.5 bg-white"
          data-testid="service-pricing-rc-select"
        >
          {rateCards.map(rc => (
            <option key={rc.id} value={rc.id}>{rc.display_name} (v{rc.version})</option>
          ))}
        </select>
      </div>

      {!rcDetail ? <TableSkeleton rows={3} /> : (
        <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
          <div className="px-4 py-3 bg-[#F3F3F3] border-b border-[#E5E5E5] flex items-center gap-2">
            <StatusPill status={rcDetail.status} />
            <span className="text-xs text-[#706E6B] font-mono">{rcDetail.rate_card_code}</span>
            <span className="text-xs text-[#706E6B]">· Effective {rcDetail.effective_from}</span>
            <span className="ml-auto text-xs text-[#706E6B]">{rcDetail.currency}</span>
          </div>
          {(rcDetail.lines || []).length === 0 ? (
            <div className="p-6 text-center text-xs text-[#706E6B]">No pricing lines configured.</div>
          ) : (
            <table className="w-full text-xs" data-testid="service-pricing-table">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Service</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Category</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Pricing Model</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Unit</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#444444]">Base Amount</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#444444]">Min Qty</th>
                </tr>
              </thead>
              <tbody>
                {rcDetail.lines.map(line => (
                  <tr key={line.line_id} className="border-b border-[#F3F3F3]">
                    <td className="px-4 py-3 font-medium text-[#181818]">
                      {line.service_name || line.service_code}
                      {line.is_price_on_request && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-[9px] bg-amber-50 text-amber-600 border border-amber-200 rounded">
                          Price on Request
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#706E6B]">{line.service_category_name || line.service_category_id || "—"}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{line.pricing_model?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{line.unit}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-[#181818]">
                      {line.is_price_on_request ? "—" : formatMinor(line.base_amount_minor, line.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-[#706E6B]">{line.min_quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3: PRICING RULES (ADJUSTMENTS)
// ═══════════════════════════════════════════════════════════════════════════════
function AdjustmentsTab({ config }) {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    pricingApi.listAdjustments(typeFilter ? { adjustment_type: typeFilter } : {})
      .then(r => setAdjustments(r.data.adjustments || []))
      .finally(() => setLoading(false));
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (adj) => {
    try {
      if (adj.status === "ACTIVE") {
        await pricingApi.deactivateAdjustment(adj.id);
        toast.success("Rule deactivated");
      } else {
        await pricingApi.activateAdjustment(adj.id);
        toast.success("Rule activated");
      }
      load();
    } catch {
      toast.error("Failed to update rule status");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-[#181818]">Pricing Rules</h2>
        <div className="flex items-center gap-2">
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="text-xs border border-[#E5E5E5] rounded-lg px-3 py-1.5 bg-white"
            data-testid="adj-type-filter"
          >
            <option value="">All Types</option>
            {(config?.adjustment_types || []).map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0176D3] text-white text-xs font-medium rounded-lg hover:bg-[#014486]"
            data-testid="create-adjustment-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New Rule
          </button>
        </div>
      </div>

      {loading ? <TableSkeleton rows={3} /> : (
        adjustments.length === 0 ? (
          <EmptyState
            icon={SlidersHorizontal}
            title="No pricing rules configured"
            description="Add seasonal, location-based or quantity adjustment rules."
            action={{ label: "Create Rule", onClick: () => setShowCreate(true) }}
          />
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
            <table className="w-full text-xs" data-testid="adjustments-table">
              <thead>
                <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Rule</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Value</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Effective</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Priority</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#444444]">Action</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map(adj => (
                  <tr key={adj.id} data-testid={`adj-row-${adj.id}`}
                    className="border-b border-[#F3F3F3] hover:bg-[#F8F9FB]">
                    <td className="px-4 py-3 font-medium text-[#181818]">{adj.display_name}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{adj.adjustment_type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 font-mono text-[#444444]">
                      {adj.adjustment_value_type === "PERCENTAGE"
                        ? `${adj.is_surcharge ? "+" : "-"}${bpsToLabel(adj.adjustment_value_bps)}`
                        : formatMinor(adj.adjustment_value_minor)}
                    </td>
                    <td className="px-4 py-3 text-[#706E6B]">{adj.effective_from}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{adj.priority}</td>
                    <td className="px-4 py-3"><StatusPill status={adj.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleStatus(adj)}
                        className={`px-2 py-1 text-[10px] font-medium rounded-lg border ${
                          adj.status === "ACTIVE"
                            ? "border-slate-200 text-slate-600 hover:bg-slate-50"
                            : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                        }`}
                        data-testid={`adj-toggle-${adj.id}`}
                      >
                        {adj.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {showCreate && (
        <CreateAdjustmentModal
          config={config}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateAdjustmentModal({ config, onClose, onCreated }) {
  const [form, setForm] = useState({
    adjustment_code: "", display_name: "", adjustment_type: "SEASONAL",
    adjustment_value_type: "PERCENTAGE", adjustment_value_bps: "",
    adjustment_value_minor: "", is_surcharge: true, priority: 10,
    effective_from: new Date().toISOString().split("T")[0], effective_to: "", notes: "",
    applies_to: { service_ids: [], service_category_ids: [], all_services: true },
    condition: { date_from: "", date_to: "", cities: [], guest_count_min: null, guest_count_max: null, event_type_codes: [] },
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.adjustment_code || !form.display_name) {
      toast.error("Code and name are required");
      return;
    }
    setSaving(true);
    try {
      await pricingApi.createAdjustment({
        ...form,
        adjustment_value_bps: form.adjustment_value_type === "PERCENTAGE" && form.adjustment_value_bps
          ? Number(form.adjustment_value_bps) : null,
        adjustment_value_minor: form.adjustment_value_type === "FIXED_AMOUNT" && form.adjustment_value_minor
          ? Number(form.adjustment_value_minor) : null,
        priority: Number(form.priority),
        effective_to: form.effective_to || null,
      });
      toast.success("Pricing rule created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create Pricing Rule" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Rule Code *">
            <input value={form.adjustment_code}
              onChange={e => setForm(f => ({ ...f, adjustment_code: e.target.value.toUpperCase() }))}
              className="input-field font-mono" placeholder="ADJ-PEAK-SEASON" data-testid="adj-code-input" />
          </FormField>
          <FormField label="Display Name *">
            <input value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              className="input-field" placeholder="Peak Season Surcharge" data-testid="adj-name-input" />
          </FormField>
          <FormField label="Adjustment Type">
            <select value={form.adjustment_type}
              onChange={e => setForm(f => ({ ...f, adjustment_type: e.target.value }))}
              className="input-field">
              {(config?.adjustment_types || []).map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Value Type">
            <select value={form.adjustment_value_type}
              onChange={e => setForm(f => ({ ...f, adjustment_value_type: e.target.value }))}
              className="input-field">
              {(config?.adjustment_value_types || []).map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </FormField>
          {form.adjustment_value_type === "PERCENTAGE" ? (
            <FormField label="Value (basis points)" help="1500 = 15%">
              <input type="number" value={form.adjustment_value_bps}
                onChange={e => setForm(f => ({ ...f, adjustment_value_bps: e.target.value }))}
                className="input-field font-mono" placeholder="1500" data-testid="adj-bps-input" />
            </FormField>
          ) : (
            <FormField label="Fixed Amount (minor units)">
              <input type="number" value={form.adjustment_value_minor}
                onChange={e => setForm(f => ({ ...f, adjustment_value_minor: e.target.value }))}
                className="input-field font-mono" placeholder="50000" />
            </FormField>
          )}
          <FormField label="Priority (1=highest)">
            <input type="number" value={form.priority}
              onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
              min={1} max={999} className="input-field" />
          </FormField>
          <FormField label="Effective From *">
            <input type="date" value={form.effective_from}
              onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
              className="input-field" />
          </FormField>
          <FormField label="Effective To">
            <input type="date" value={form.effective_to}
              onChange={e => setForm(f => ({ ...f, effective_to: e.target.value }))}
              className="input-field" />
          </FormField>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={form.is_surcharge}
            onChange={e => setForm(f => ({ ...f, is_surcharge: e.target.checked }))} />
          Is surcharge (adds to price). Uncheck for discount.
        </label>
        <FormField label="Notes">
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} className="input-field resize-none" />
        </FormField>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="adj-save-btn">
            {saving ? "Creating..." : "Create Rule"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4: TAX & CURRENCY
// ═══════════════════════════════════════════════════════════════════════════════
function TaxCurrencyTab() {
  const [taxMappings, setTaxMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    pricingApi.listTaxMappings()
      .then(r => setTaxMappings(r.data.tax_mappings || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deactivate = async (tid) => {
    try {
      await pricingApi.deactivateTaxMapping(tid);
      toast.success("Tax mapping deactivated");
      load();
    } catch {
      toast.error("Failed to deactivate");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#181818]">Tax & Currency Mapping</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0176D3] text-white text-xs font-medium rounded-lg hover:bg-[#014486]"
          data-testid="create-tax-mapping-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New Tax Mapping
        </button>
      </div>

      {loading ? <TableSkeleton rows={2} /> : (
        taxMappings.length === 0 ? (
          <EmptyState
            icon={Percent}
            title="No tax mappings configured"
            description="Configure GST, VAT or other applicable tax rates for your services."
            action={{ label: "Add Tax Mapping", onClick: () => setShowCreate(true) }}
          />
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
            <table className="w-full text-xs" data-testid="tax-mappings-table">
              <thead>
                <tr className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Tax Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Type</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#444444]">Rate</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Applies To</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Effective</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#444444]">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#444444]">Action</th>
                </tr>
              </thead>
              <tbody>
                {taxMappings.map(tax => (
                  <tr key={tax.id} data-testid={`tax-row-${tax.id}`}
                    className="border-b border-[#F3F3F3] hover:bg-[#F8F9FB]">
                    <td className="px-4 py-3 font-medium text-[#181818]">{tax.display_name}</td>
                    <td className="px-4 py-3 font-mono text-[#706E6B]">{tax.tax_code}</td>
                    <td className="px-4 py-3 text-[#706E6B]">{tax.tax_type}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-[#181818]">
                      {bpsToLabel(tax.tax_rate_bps)}
                    </td>
                    <td className="px-4 py-3 text-[#706E6B]">
                      {tax.applies_to_all_services ? "All Services" : "Specific Services"}
                    </td>
                    <td className="px-4 py-3 text-[#706E6B]">{tax.effective_from}</td>
                    <td className="px-4 py-3"><StatusPill status={tax.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {tax.status === "ACTIVE" && (
                        <button
                          onClick={() => deactivate(tax.id)}
                          className="px-2 py-1 text-[10px] border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                          data-testid={`tax-deactivate-${tax.id}`}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {showCreate && (
        <CreateTaxMappingModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateTaxMappingModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    tax_code: "", display_name: "", tax_type: "GST",
    tax_rate_bps: "", applies_to_all_services: true,
    is_inclusive: false,
    effective_from: new Date().toISOString().split("T")[0],
    effective_to: "", legal_reference: "", rounding_policy: "ROUND_HALF_UP", notes: "",
    applies_to_service_category_ids: [], applies_to_service_ids: [], location_scope: null,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.tax_code || !form.display_name || !form.tax_rate_bps) {
      toast.error("Code, name and tax rate are required");
      return;
    }
    setSaving(true);
    try {
      await pricingApi.createTaxMapping({
        ...form,
        tax_rate_bps: Number(form.tax_rate_bps),
        effective_to: form.effective_to || null,
      });
      toast.success("Tax mapping created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create tax mapping");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create Tax Mapping" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Tax Code *">
            <input value={form.tax_code}
              onChange={e => setForm(f => ({ ...f, tax_code: e.target.value.toUpperCase() }))}
              className="input-field font-mono" placeholder="GST-18" data-testid="tax-code-input" />
          </FormField>
          <FormField label="Display Name *">
            <input value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              className="input-field" placeholder="GST 18% — Event Services" data-testid="tax-name-input" />
          </FormField>
          <FormField label="Tax Type">
            <select value={form.tax_type}
              onChange={e => setForm(f => ({ ...f, tax_type: e.target.value }))}
              className="input-field">
              {["GST", "VAT", "SERVICE_TAX", "CUSTOM"].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Tax Rate (basis points) *" help="1800 = 18.00%">
            <input type="number" value={form.tax_rate_bps}
              onChange={e => setForm(f => ({ ...f, tax_rate_bps: e.target.value }))}
              className="input-field font-mono" placeholder="1800" data-testid="tax-rate-input" />
          </FormField>
          <FormField label="Effective From *">
            <input type="date" value={form.effective_from}
              onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
              className="input-field" />
          </FormField>
          <FormField label="Rounding Policy">
            <select value={form.rounding_policy}
              onChange={e => setForm(f => ({ ...f, rounding_policy: e.target.value }))}
              className="input-field">
              {["ROUND_HALF_UP", "ROUND_DOWN", "ROUND_UP"].map(r => (
                <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
              ))}
            </select>
          </FormField>
        </div>
        <FormField label="Legal Reference">
          <input value={form.legal_reference}
            onChange={e => setForm(f => ({ ...f, legal_reference: e.target.value }))}
            className="input-field" placeholder="e.g. CGST 9% + SGST 9% under HSN 9985" />
        </FormField>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={form.is_inclusive}
            onChange={e => setForm(f => ({ ...f, is_inclusive: e.target.checked }))} />
          Tax-inclusive pricing (tax already included in base amount)
        </label>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="tax-save-btn">
            {saving ? "Creating..." : "Create Tax Mapping"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 5: ESTIMATE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════
function EstimateConfigTab() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    pricingApi.getEstimateConfig()
      .then(r => { setConfig(r.data); setForm(r.data); })
      .finally(() => setLoading(false));
  }, []);

  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        disclaimer_text: form.disclaimer_text,
        disclaimer_version: form.disclaimer_version,
        contingency_pct_bps: Number(form.contingency_pct_bps),
        estimate_validity_days: Number(form.estimate_validity_days),
        show_tax_line: form.show_tax_line,
        show_range: form.show_range,
        range_lower_pct_bps: Number(form.range_lower_pct_bps),
        range_upper_pct_bps: Number(form.range_upper_pct_bps),
        price_on_request_label: form.price_on_request_label,
      };
      const r = await pricingApi.updateEstimateConfig(payload);
      setConfig(r.data);
      setForm(r.data);
      setDirty(false);
      toast.success("Estimate configuration saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <TableSkeleton rows={5} />;
  if (!form) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#181818]">Estimate Configuration</h2>
        {dirty && (
          <span className="text-[10px] text-amber-600 font-mono bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
            Unsaved changes
          </span>
        )}
      </div>

      {/* Disclaimer */}
      <section className="bg-white rounded-xl border border-[#E5E5E5] p-5 space-y-4">
        <h3 className="text-sm font-semibold text-[#181818]">Guest-Facing Disclaimer</h3>
        <FormField label="Disclaimer Text (shown on every estimate)">
          <textarea
            value={form.disclaimer_text || ""}
            onChange={e => update("disclaimer_text", e.target.value)}
            rows={4}
            className="input-field resize-none text-xs"
            data-testid="disclaimer-text-input"
          />
        </FormField>
        <FormField label="Disclaimer Version">
          <input value={form.disclaimer_version || ""}
            onChange={e => update("disclaimer_version", e.target.value)}
            className="input-field font-mono text-xs" />
        </FormField>
        <FormField label="Price on Request Label">
          <input value={form.price_on_request_label || ""}
            onChange={e => update("price_on_request_label", e.target.value)}
            className="input-field text-xs" data-testid="por-label-input" />
        </FormField>
      </section>

      {/* Range & Contingency */}
      <section className="bg-white rounded-xl border border-[#E5E5E5] p-5 space-y-4">
        <h3 className="text-sm font-semibold text-[#181818]">Estimate Range Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Contingency (basis points)" help="e.g. 1000 = 10% contingency buffer">
            <input type="number" value={form.contingency_pct_bps || 0}
              onChange={e => update("contingency_pct_bps", e.target.value)}
              className="input-field font-mono" data-testid="contingency-bps-input" />
          </FormField>
          <FormField label="Estimate Validity (days)">
            <input type="number" value={form.estimate_validity_days || 30}
              onChange={e => update("estimate_validity_days", e.target.value)}
              className="input-field font-mono" min={1} max={365} />
          </FormField>
          <FormField label="Range Lower (basis points)" help="-1000 = -10% (lower bound)">
            <input type="number" value={form.range_lower_pct_bps || 0}
              onChange={e => update("range_lower_pct_bps", e.target.value)}
              className="input-field font-mono" />
          </FormField>
          <FormField label="Range Upper (basis points)" help="1500 = +15% (upper bound)">
            <input type="number" value={form.range_upper_pct_bps || 0}
              onChange={e => update("range_upper_pct_bps", e.target.value)}
              className="input-field font-mono" />
          </FormField>
        </div>
      </section>

      {/* Display Options */}
      <section className="bg-white rounded-xl border border-[#E5E5E5] p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[#181818]">Display Options</h3>
        <label className="flex items-center gap-3 text-xs text-[#444444]">
          <input type="checkbox" checked={form.show_tax_line || false}
            onChange={e => update("show_tax_line", e.target.checked)}
            className="rounded" data-testid="show-tax-toggle" />
          Show tax line item in guest estimate breakdown
        </label>
        <label className="flex items-center gap-3 text-xs text-[#444444]">
          <input type="checkbox" checked={form.show_range || false}
            onChange={e => update("show_range", e.target.checked)}
            className="rounded" data-testid="show-range-toggle" />
          Show indicative range (lower–upper bound) to guest
        </label>
      </section>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="btn-primary disabled:opacity-50"
          data-testid="estimate-config-save-btn"
        >
          {saving ? "Saving..." : "Save Configuration"}
        </button>
        {dirty && (
          <button onClick={() => { setForm(config); setDirty(false); }} className="btn-secondary">
            Discard Changes
          </button>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 6: VERSION HISTORY
// ═══════════════════════════════════════════════════════════════════════════════
function VersionHistoryTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    pricingApi.getVersionHistory(entityFilter ? { entity_type: entityFilter } : {})
      .then(r => setEvents(r.data.history || []))
      .finally(() => setLoading(false));
  }, [entityFilter]);

  useEffect(() => { load(); }, [load]);

  const EVENT_ICONS = {
    PUBLISHED:  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
    ARCHIVED:   <Archive className="w-3.5 h-3.5 text-slate-400" />,
    CREATED:    <Plus className="w-3.5 h-3.5 text-[#0176D3]" />,
    UPDATED:    <Pencil className="w-3.5 h-3.5 text-amber-500" />,
    SUBMITTED:  <Send className="w-3.5 h-3.5 text-blue-500" />,
  };

  const getIcon = (eventType) => {
    for (const [key, icon] of Object.entries(EVENT_ICONS)) {
      if (eventType?.includes(key)) return icon;
    }
    return <Clock className="w-3.5 h-3.5 text-[#706E6B]" />;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#181818]">Publish & Version History</h2>
        <div className="flex items-center gap-2">
          <select
            value={entityFilter}
            onChange={e => setEntityFilter(e.target.value)}
            className="text-xs border border-[#E5E5E5] rounded-lg px-3 py-1.5 bg-white"
            data-testid="history-entity-filter"
          >
            <option value="">All Changes</option>
            <option value="rate_card">Rate Cards</option>
            <option value="adjustment">Pricing Rules</option>
            <option value="tax_mapping">Tax Mappings</option>
            <option value="estimate_config">Estimate Config</option>
          </select>
          <button onClick={load} className="p-1.5 border border-[#E5E5E5] rounded-lg text-[#706E6B] hover:text-[#0176D3]">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {loading ? <TableSkeleton rows={5} /> : (
        events.length === 0 ? (
          <EmptyState
            icon={History}
            title="No audit history yet"
            description="Pricing configuration changes will appear here once you start managing rate cards and rules."
          />
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E5E5] divide-y divide-[#F3F3F3]">
            {events.map(ev => (
              <div key={ev.id} data-testid={`history-event-${ev.id}`}
                className="flex items-start gap-3 px-4 py-3">
                <div className="mt-0.5">{getIcon(ev.event_type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-[#444444]">{ev.event_type}</span>
                    {ev.entity_id && (
                      <span className="text-[10px] font-mono text-[#706E6B] truncate">{ev.entity_id}</span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#706E6B] font-mono mt-0.5">
                    {ev.actor_id} · {ev.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB 7: PRICING SIMULATOR (GQP-02 placeholder)
// ═══════════════════════════════════════════════════════════════════════════════
function SimulatorTab() {
  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-xl border border-[#E5E5E5] p-8 text-center space-y-4">
        <div className="w-14 h-14 bg-[#EEF4FF] rounded-2xl flex items-center justify-center mx-auto">
          <FlaskConical className="w-7 h-7 text-[#0176D3]" />
        </div>
        <h2 className="text-base font-semibold text-[#181818]">Pricing Simulator</h2>
        <p className="text-sm text-[#706E6B] max-w-sm mx-auto">
          The Pricing Simulator will be available in <strong>GQP-02</strong> after the server-side
          estimate calculation engine is deployed.
        </p>
        <div className="bg-[#F3F3F3] rounded-lg p-4 text-left text-xs text-[#706E6B] space-y-2">
          <p className="font-semibold text-[#444444]">GQP-02 will provide:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Server-side Decimal estimate calculations (no float arithmetic)</li>
            <li>Published rate card selection by effective date</li>
            <li>Seasonal and location adjustment application</li>
            <li>Tax calculation using configured mappings</li>
            <li>Indicative estimate range generation</li>
          </ul>
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-[#706E6B]">
          <Info className="w-3.5 h-3.5" />
          Current stage: GQP-01 (Pricing Foundation) — Complete
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════
function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] ${wide ? "w-full max-w-3xl" : "w-full max-w-lg"}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-semibold text-[#181818]">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-[#706E6B] hover:text-[#181818] hover:bg-[#F3F3F3] rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, children, help }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-[#444444]">{label}</label>
      {help && <p className="text-[10px] text-[#706E6B]">{help}</p>}
      {children}
    </div>
  );
}

function InfoCard({ label, value, mono = false }) {
  return (
    <div className="bg-[#F3F3F3] rounded-lg p-3">
      <p className="text-[10px] text-[#706E6B] font-mono uppercase tracking-wide mb-1">{label}</p>
      <div className={`text-xs font-medium text-[#181818] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E5E5] p-10 text-center space-y-3">
      <div className="w-10 h-10 bg-[#EEF4FF] rounded-xl flex items-center justify-center mx-auto">
        <Icon className="w-5 h-5 text-[#0176D3]" />
      </div>
      <p className="text-sm font-semibold text-[#181818]">{title}</p>
      <p className="text-xs text-[#706E6B] max-w-xs mx-auto">{description}</p>
      {action && (
        <button onClick={action.onClick}
          className="mt-2 px-4 py-2 bg-[#0176D3] text-white text-xs font-medium rounded-lg hover:bg-[#014486]">
          {action.label}
        </button>
      )}
    </div>
  );
}

function TableSkeleton({ rows = 3 }) {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-9 bg-[#F3F3F3] rounded-lg" />
      {[...Array(rows)].map((_, i) => <div key={i} className="h-12 bg-[#F3F3F3] rounded-lg" />)}
    </div>
  );
}

function Spinner() {
  return <div className="w-6 h-6 border-2 border-[#0176D3] border-t-transparent rounded-full animate-spin" />;
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-[#F8F9FB] animate-pulse">
      <div className="h-16 bg-white border-b border-[#E5E5E5]" />
      <div className="h-11 bg-white border-b border-[#E5E5E5]" />
      <div className="p-6 space-y-4 max-w-7xl mx-auto">
        <div className="h-8 bg-[#F3F3F3] rounded-lg w-48" />
        <div className="h-64 bg-[#F3F3F3] rounded-xl" />
      </div>
    </div>
  );
}
